import { useEffect, useState, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Users2, UserPlus, CalendarDays, Crown, AlertTriangle, CalendarClock,
  Clock, Trophy, XCircle, TrendingUp, IndianRupee, CheckCircle2, Gauge, Timer,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView } from "@/components/StateViews";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fmtINR } from "@/lib/format";
import DateRangePicker from "@/components/DateRangePicker";
import ExecDashboard from "@/pages/ExecDashboard";

const COLORS = ["#2C78FF", "#072A73", "#10B981", "#8B5CF6", "#3B82F6", "#F59E0B", "#EF4444", "#14B8A6"];

// Humanise a business-hours minute count into "Xm" / "Xh Ym" / "Xh".
function fmtMins(mins) {
  const m = Math.round(mins || 0);
  if (m <= 0) return "0m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

const CARD_DEFS = [
  { key: "total_leads", label: "Total Leads", icon: Users2, color: "text-loop", bg: "bg-sky/50" },
  { key: "new_today", label: "New Today", icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "new_week", label: "New This Week", icon: CalendarDays, color: "text-navy", bg: "bg-mist" },
  { key: "ace_king", label: "Ace + King Leads", icon: Crown, color: "text-amber-500", bg: "bg-amber-50" },
  { key: "ace_king_pipeline", label: "Ace+King Pipeline (Expected)", icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50", currency: true },
  { key: "sales_in_range", label: "Sales (Selected Range)", icon: IndianRupee, color: "text-emerald-600", bg: "bg-emerald-50", currency: true, dynamicLabel: true },
  { key: "due_today", label: "Activities Due Today", icon: CalendarClock, color: "text-loop", bg: "bg-sky/50" },
  { key: "overdue", label: "Overdue Activities", icon: Clock, color: "text-red-500", bg: "bg-red-50" },
  { key: "completed_week", label: "Completed This Week", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "won", label: "Won", icon: Trophy, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "lost", label: "Lost", icon: XCircle, color: "text-slate-400", bg: "bg-slate-100" },
  { key: "unassigned", label: "Unassigned", icon: AlertTriangle, color: "text-orange-500", bg: "bg-orange-50", hideForExec: true },
];

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-5">
      <h3 className="mb-4 font-sora text-sm font-semibold text-navy">{title}</h3>
      {children}
    </div>
  );
}

const BarBlock = ({ data, testid }) => {
  if (!data?.length || data.every((d) => !d.value)) return <p data-testid={testid} className="py-8 text-center text-sm text-ink/40">No data</p>;
  return (
    <div data-testid={testid}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 5, right: 8, bottom: 5, left: -20 }}>
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#10224A" }} interval={0} angle={-25} textAnchor="end" height={60} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
          <Tooltip cursor={{ fill: "#F4F6FA" }} contentStyle={{ borderRadius: 12, border: "1px solid #DCE8FF", fontSize: 12 }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const DonutBlock = ({ data }) => {
  const filtered = (data || []).filter((d) => d.value > 0);
  if (!filtered.length) return <p className="py-8 text-center text-sm text-ink/40">No data</p>;
  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width="55%" height={200}>
        <PieChart>
          <Pie data={filtered} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
            {filtered.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DCE8FF", fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-1.5">
        {filtered.map((d, i) => (
          <div key={`${d.name}-${i}`} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="flex-1 text-ink/70">{d.name}</span>
            <span className="font-semibold text-navy">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "Sales Executive") return <ExecDashboard />;
  return <ManagerDashboard />;
}

function ManagerDashboard() {
  const { user, options } = useAuth();
  const [data, setData] = useState(null);
  const [qa, setQa] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ created_from: "", created_to: "", service_interested: "", business_segment: "" });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await minDelay(api.get("/dashboard", { params }));
      setData(data.data);
      try {
        const { data: s } = await api.get("/dashboard/sla", { params });
        setSla(s.data);
      } catch { setSla(null); }
      if (user?.role !== "Sales Executive") {
        try {
          const { data: q } = await api.get("/dashboard/quality-accuracy", { params });
          setQa(q.data);
        } catch { setQa(null); }
      }
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [filters, user]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Loading label="Loading dashboard..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const c = data.cards;
  const ch = data.charts;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-sora text-3xl font-bold text-navy">Dashboard</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">
            Welcome back, {user?.name?.split(" ")[0]} · here's your pipeline at a glance.
            {loading && data && <span data-testid="dashboard-refreshing" className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker
            from={filters.created_from}
            to={filters.created_to}
            onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))}
            testid="dash-date-range"
          />
          <select value={filters.service_interested} onChange={(e) => setFilters((f) => ({ ...f, service_interested: e.target.value }))} className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
            <option value="">All services</option>
            {options?.services?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Metric cards */}
      <div className={`grid grid-cols-2 gap-3 transition-opacity sm:grid-cols-3 lg:grid-cols-5 ${loading && data ? "opacity-50" : ""}`}>
        {CARD_DEFS.filter((cd) => !(cd.hideForExec && user?.role === "Sales Executive")).map((cd) => {
          const label = cd.dynamicLabel
            ? (user?.role === "Sales Executive" ? "My Sales (Range)" : "Team Sales (Range)")
            : cd.label;
          const value = cd.currency ? fmtINR(c[cd.key]) : (c[cd.key] ?? 0);
          return (
            <div key={cd.key} data-testid={`metric-${cd.key}`} className="rounded-2xl border border-sky/70 bg-white p-4 transition-shadow hover:shadow-md">
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl ${cd.bg} ${cd.color}`}>
                <cd.icon className="h-[18px] w-[18px]" />
              </div>
              <p className={`font-sora font-bold text-navy ${cd.currency ? "text-xl" : "text-2xl"}`}>{value}</p>
              <p className="text-xs text-ink/60">{label}</p>
            </div>
          );
        })}
      </div>

      {/* Lead Response SLA (business hours only · admin-configurable window) */}
      {sla && (
        <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="sla-report">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-sky/60 text-loop"><Timer className="h-[18px] w-[18px]" /></span>
              <div>
                <h3 className="font-sora text-sm font-semibold text-navy">Lead Response SLA</h3>
                <p className="text-xs text-ink/50">Target {fmtMins(sla.sla_target_minutes)} (business hours). At-risk if a generated lead isn't contacted within target.</p>
              </div>
            </div>
            <span data-testid="sla-reshuffle-state" className={`hidden rounded-full border px-2.5 py-1 text-[11px] font-semibold sm:inline-flex ${sla.auto_reshuffle ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
              Auto-reshuffle {sla.auto_reshuffle ? "ON" : "OFF"}
            </span>
          </div>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: "SLA Breach Rate", value: `${sla.summary.breach_rate}%`, testid: "sla-breach-rate", accent: sla.summary.breach_rate > 30 ? "text-red-500" : "text-emerald-600" },
              { label: "Crossed SLA", value: sla.summary.breached_count, testid: "sla-breached", accent: sla.summary.breached_count > 0 ? "text-red-500" : "text-navy" },
              { label: "At-risk Now", value: sla.summary.at_risk_now, testid: "sla-at-risk", accent: sla.summary.at_risk_now > 0 ? "text-amber-600" : "text-navy" },
              { label: "Avg First Response", value: fmtMins(sla.summary.avg_time_to_first_response_mins), testid: "sla-avg-response", sub: `median ${fmtMins(sla.summary.median_time_to_first_response_mins)}`, accent: "text-loop" },
              { label: "Unassigned Now", value: sla.summary.unassigned_now, testid: "sla-unassigned", accent: sla.summary.unassigned_now > 0 ? "text-orange-500" : "text-navy" },
              { label: "Held (off-hours)", value: sla.summary.deferred_now, testid: "sla-deferred", accent: sla.summary.deferred_now > 0 ? "text-violet-600" : "text-navy" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="rounded-xl border border-sky/60 bg-mist/40 p-3">
                <p className={`font-sora text-xl font-bold ${s.accent || "text-navy"}`}>{s.value}</p>
                <p className="text-[11px] text-ink/60">{s.label}</p>
                {s.sub && <p className="text-[10px] text-ink/40">{s.sub}</p>}
              </div>
            ))}
          </div>
          {(sla.summary.deferred_now > 0 || sla.summary.unassigned_now > 0) && user?.role !== "Sales Executive" && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="sla-unassigned-banner">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                {sla.summary.deferred_now > 0 && `${sla.summary.deferred_now} lead(s) are held until the office reopens and will auto-assign. `}
                {sla.summary.unassigned_now > 0 && `${sla.summary.unassigned_now} lead(s) need manual assignment — `}
                {sla.summary.unassigned_now > 0 && <a href="/leads?assigned=false" className="font-semibold underline" data-testid="sla-view-unassigned">view &amp; assign</a>}
              </span>
            </div>
          )}
          <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <ChartCard title="Time to Assign (business hrs)"><BarBlock data={sla.assign_buckets} testid="sla-assign-buckets" /></ChartCard>
            <ChartCard title="Time to First Response (business hrs)"><BarBlock data={sla.response_buckets} testid="sla-response-buckets" /></ChartCard>
            <ChartCard title="Avg Response by Salesperson (mins)"><BarBlock data={sla.by_person} testid="sla-by-person" /></ChartCard>
          </div>
          {/* Per-salesperson SLA breach report (Admin & Manager) */}
          {user?.role !== "Sales Executive" && (
            <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid="sla-by-person-table">
              <h4 className="mb-3 font-sora text-sm font-semibold text-navy">SLA Breach by Salesperson</h4>
              {sla.sla_by_person?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-sky/60 text-left text-xs text-ink/50">
                        <th className="px-3 py-2">Salesperson</th>
                        <th className="px-3 py-2 text-right">Leads Handled</th>
                        <th className="px-3 py-2 text-right">Crossed SLA</th>
                        <th className="px-3 py-2 text-right">Breach %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sky/40">
                      {sla.sla_by_person.map((r) => (
                        <tr key={r.user_id || r.name} data-testid={`sla-person-row-${r.name}`}>
                          <td className="px-3 py-2 font-medium text-navy">{r.name}</td>
                          <td className="px-3 py-2 text-right text-ink/70">{r.handled}</td>
                          <td className="px-3 py-2 text-right text-ink/70">{r.breached}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${r.breach_rate > 30 ? "text-red-500" : r.breach_rate > 0 ? "text-amber-600" : "text-emerald-600"}`}>{r.breach_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-ink/40">No assigned leads in this range yet.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quality Accuracy mini-report (manager-only) */}
      {qa && user?.role !== "Sales Executive" && (
        <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="quality-accuracy-report">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-50 text-violet-600"><Gauge className="h-[18px] w-[18px]" /></span>
            <div>
              <h3 className="font-sora text-sm font-semibold text-navy">Quality Accuracy</h3>
              <p className="text-xs text-ink/50">How well auto-scoring matches manager judgement — a coaching & tuning signal.</p>
            </div>
          </div>

          {qa.total_qualifications === 0 ? (
            <p className="py-6 text-center text-sm text-ink/40">No qualifications recorded in this range yet.</p>
          ) : (
            <>
              <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {[
                  { label: "Qualifications", value: qa.total_qualifications, testid: "qa-quals" },
                  { label: "Manual Overrides", value: qa.total_overrides, testid: "qa-overrides" },
                  { label: "Override Rate", value: `${qa.override_rate}%`, testid: "qa-rate", accent: qa.override_rate > 25 ? "text-red-500" : "text-emerald-600" },
                  { label: "Upgraded", value: qa.upgrades, testid: "qa-upgrades", accent: "text-emerald-600" },
                  { label: "Downgraded", value: qa.downgrades, testid: "qa-downgrades", accent: "text-orange-500" },
                  { label: "Needs Review", value: qa.needs_review, testid: "qa-needs-review", accent: "text-amber-600" },
                ].map((s) => (
                  <div key={s.label} data-testid={s.testid} className="rounded-xl border border-sky/60 bg-mist/40 p-3">
                    <p className={`font-sora text-xl font-bold ${s.accent || "text-navy"}`}>{s.value}</p>
                    <p className="text-[11px] text-ink/60">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <ChartCard title="Auto-scored Quality Mix"><DonutBlock data={qa.quality_distribution} /></ChartCard>
                <ChartCard title="Most-Triggered Rules & Caps"><BarBlock data={qa.top_rules} testid="qa-top-rules" /></ChartCard>
                <ChartCard title="Overrides by Executive"><BarBlock data={qa.overrides_by_executive} testid="qa-overrides-by-exec" /></ChartCard>
              </div>
            </>
          )}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Leads by Stage"><BarBlock data={ch.by_stage} testid="chart-by-stage" /></ChartCard>
        <ChartCard title="Leads by Quality"><DonutBlock data={ch.by_quality} /></ChartCard>
        <ChartCard title="Leads by Service Interested"><BarBlock data={ch.by_service} /></ChartCard>
        <ChartCard title="Leads by Platform"><DonutBlock data={ch.by_platform} /></ChartCard>
        <ChartCard title="Top 5 Campaigns by Volume"><BarBlock data={ch.top_campaigns} /></ChartCard>
        <ChartCard title="Salesperson Lead Load"><BarBlock data={ch.salesperson_load} /></ChartCard>
        <ChartCard title="Activities by Type"><BarBlock data={ch.activities_by_type} /></ChartCard>
        <ChartCard title="Activities by Salesperson"><BarBlock data={ch.activities_by_person} /></ChartCard>
        {ch.overdue_by_person?.length > 0 && (
          <ChartCard title="Overdue Activities by Salesperson"><BarBlock data={ch.overdue_by_person} /></ChartCard>
        )}
        {user?.role !== "Sales Executive" && ch.sales_by_person?.length > 0 && (
          <ChartCard title="Sales by Salesperson (₹, selected range)"><BarBlock data={ch.sales_by_person} /></ChartCard>
        )}
      </div>
    </div>
  );
}
